/**
 * ProductCarousel Component - Updated to work with existing structure and cart functionality
 * Integrates with your existing ProductCarousel while adding cart support
 */

import React, { useRef } from 'react';
import ProductCard from './ProductCard.jsx';
import { usePaginatedProducts } from '../../hooks/useProducts.js';
import { useMedRecFilter } from '../../context/MedRecFilterContext.jsx';

/**
 * @param {object} props
 * @param {number} [props.categoryId] - Filter by category
 * @param {number} [props.brandId] - Filter by brand
 * @param {string} [props.categoryName] - Display name for the section
 * @param {string} [props.sortBy] - OData sort field(s), e.g. 'brandDisplayOrder, productDisplayOrder, productName'
 */
const ProductCarousel = ({ categoryId, brandId, categoryName, sortBy = 'productName' }) => {
  const scrollContainerRef = useRef(null);

  // Get Med/Rec filter from global context
  const { filterValues } = useMedRecFilter();

  const {
    products,
    isLoading,
    error,
    hasNextPage
  } = usePaginatedProducts({
    categoryId,
    brandId,
    pageSize: 50,
    inStock: true,
    sortBy,
    sortDirection: 'asc',
    isMed: filterValues.isMed,
    isRec: filterValues.isRec,
  });

  if (error) {
    console.error(`ProductCarousel error for ${categoryName}:`, error);
    return (
      <div className="product-carousel product-carousel--error">
        <h3 className="product-carousel__title">{categoryName}</h3>
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error loading products for {categoryName}: {error.message}
        </div>
      </div>
    );
  }

  if (products.length === 0 && !isLoading) {
    return (
      <div className="product-carousel product-carousel--empty">
        <div className="text-muted text-center py-4">
          <i className="bi bi-box-seam fs-2 mb-2"></i>
          <p>No products available in {categoryName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-carousel">
      <div className="product-carousel__header">
        <div className="product-carousel__meta">
          <span className="product-carousel__count">
            {products.length} product{products.length !== 1 ? 's' : ''}
            {hasNextPage && ` (showing first ${products.length})`}
          </span>
        </div>
      </div>

      <div
        className="product-carousel__container"
        ref={scrollContainerRef}
      >
        <div className="product-carousel__track">
          {products.map((product) => (
            <div key={product.id} className="product-carousel__item">
              <ProductCard product={product} showAddToCart={true} />
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="product-carousel__item product-carousel__item--loading">
              <div className="product-carousel__loading">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0 small text-muted">Loading products...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCarousel;